<script setup lang="ts">
import { computed } from 'vue';
import { useFlowStore } from '@/stores/flow';
import BrandHeader from '@/components/BrandHeader.vue';
import StepIndicator from '@/components/StepIndicator.vue';
import CodeEntry from '@/components/CodeEntry.vue';
import ChooseAuth from '@/components/ChooseAuth.vue';
import SignIn from '@/components/SignIn.vue';
import CreateAccount from '@/components/CreateAccount.vue';
import Confirming from '@/components/Confirming.vue';
import Done from '@/components/Done.vue';

const flow = useFlowStore();

const currentStep = computed(() => flow.step);

const stepIndex = computed<number>(() => {
  switch (flow.step) {
    case 'enter-code': return 0;
    case 'choose-auth':
    case 'sign-in':
    case 'create-account': return 1;
    case 'confirming':
    case 'done': return 2;
  }
  return 0;
});
</script>

<template>
  <div class="app">
    <BrandHeader />

    <main class="page">
      <div class="card">
        <StepIndicator :step="stepIndex" />

        <Transition name="fade" mode="out-in">
          <CodeEntry        v-if="currentStep === 'enter-code'" />
          <ChooseAuth       v-else-if="currentStep === 'choose-auth'" />
          <SignIn           v-else-if="currentStep === 'sign-in'" />
          <CreateAccount    v-else-if="currentStep === 'create-account'" />
          <Confirming       v-else-if="currentStep === 'confirming'" />
          <Done             v-else-if="currentStep === 'done'" />
        </Transition>
      </div>
    </main>

    <footer class="foot">
      <span>© Lime &amp; The Coconut · Demo environment</span>
      <a href="/">Back to home</a>
    </footer>
  </div>
</template>

<style scoped>
.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.page {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(1.5rem, 5vw, 4rem) 1.25rem;
}

.card {
  background: var(--cream);
  border: 1px solid var(--coco-100);
  border-radius: 22px;
  box-shadow: 0 18px 48px rgba(45, 27, 14, 0.08), 0 6px 16px rgba(45, 27, 14, 0.05);
  padding: clamp(1.5rem, 4vw, 2.5rem);
  width: 100%;
  max-width: 36rem;
}

.foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 1.25rem clamp(1.25rem, 5vw, 4rem);
  border-top: 1px solid var(--coco-100);
  background: var(--coco-50);
  font-size: 0.88rem;
  color: var(--ink-mute);
}
.foot a {
  color: var(--coco-700);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 180ms var(--ease), transform 180ms var(--ease);
}
.fade-enter-from {
  opacity: 0;
  transform: translateY(6px);
}
.fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
